import React, { useState } from "react";
import { type BuildAndBuyDispatchType, useBnbSelector } from "../../../../redux/store";
import Price from "../../common/price/Price";
import PriceFootNote from "../../common/price/PriceFootNote";
import { getGrLogo } from "../../../../../../../common-deprecated/utils";
import { type BuildAndBuyLabelType, BnbLabel, useBnbOptionalLabel } from "../../../../utils/labels";
import EcoTag from "../../common/EcoTag";
import ExteriorSpin from "./ExteriorSpin";
import InteriorGallery from "../../common/interior/InteriorGallery";
import Personalization from "./Personalization";
import Container from "../../../../../../../common-deprecated/styles/v2/toyota/globals/Container";
import * as Type from "../../../../../../../common-deprecated/styles/v2/toyota/globals/Type";
import * as Styles from "./styles/CarHero";
import WinterWheelToggle from "../../common/WinterWheelToggle";
import { IconWrapper } from "../../../../../../../common-deprecated/styles/v2/toyota/globals/IconWrapper";
import InteriorSpin3D from "../../common/interior/InteriorSpin3D";
import useWebGLSupportLevel from "../../../../hooks/customize/useWebGLSupportLevel";
import CustomizeCtaContainer from "../../common/cta/CustomizeCtaContainer";
import {
    ExteriorSpin3DState,
    setExteriorSpin3D,
    setFinanceOption,
} from "../../../../../../../common-deprecated/redux/actions/UtilActions";
import { hasWinterWheelInBasket } from "../../../../utils/typedUtils";
import { getEcoBadgeType, isRTL } from "../../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { NavStep } from "../../../../../shared-logic/utils/Navigation";
import Icon from "../../../../../../../common-deprecated/styles/v2/toyota/globals/Icon";
import Icon3D from "../../../../../../../common-deprecated/svg/v2/toyota/3dview.svg";
import { GrCode, MAIN_NAV_CLASSNAME_TOYOTA } from "../../../../../../../common-deprecated/constants";
import Exterior3DSpin from "./Exterior3DSpin";
import UnsetZIndexHelper from "../../../../../../../common-deprecated/components/UnsetZIndexHelper";
import GRLogo from "../../../../../../../../assets/toyota/svg/GR/TME-GR.svg";
import GRLogoSport from "../../../../../../../../assets/toyota/svg/GR/TME-GR-sport-black.svg";
import { shouldHideCarHeaderPrice, showNextGenInteriorDisclaimer } from "../../../../utils/settings/bnbSettingUtils";
import { trackCustomizeFullscreen, trackFinanceToggle } from "../../../../utils/tracking";
import EcoBonusPromotion from "../../../../../../../common-deprecated/components/aem/EcoBonusPromotion";
import { getEcoBonusPromotions } from "../../../../../../../common-deprecated/utils/promotionUtils";
import type { PricePromotionType } from "../../../../../../../common-deprecated/types/CommonTypes";
import { getEcoTagType } from "../../../../../../shared-logic/utils/typedUtils";
import AvailabilityBadges from "../../../../../../../common-deprecated/components/aem/toyota/availability-badges/AvailabilityBadges";
import { getInteriorSpinFlags } from "../../../../../../shared-logic/utils/imageUtils";
import { useDispatch } from "../../../../../../../shared-logic/redux/store";

const exterior3DSpinLabelMap: Record<ExteriorSpin3DState, BuildAndBuyLabelType> = {
    [ExteriorSpin3DState.Displayed]: "spin2DView",
    [ExteriorSpin3DState.Hidden]: "spin3DView",
    [ExteriorSpin3DState.Error]: "spin3DError",
};

const CarHero = (): JSX.Element => {
    const [spinFullscreen, setSpinFullscreen] = useState<boolean>(false);
    const dispatch = useDispatch<BuildAndBuyDispatchType>();

    const commonSettings = useBnbSelector((state) => state.commonSettings);
    const car = useBnbSelector((state) => state.car);
    const activePersonalization = useBnbSelector((state) => state.utils.activePersonalization);
    const hideCarHeaderPrice = useBnbSelector(({ bnbSettings, utils }) => shouldHideCarHeaderPrice(bnbSettings, utils));
    const hasWinterWheel = useBnbSelector((state) => hasWinterWheelInBasket(state.basket));
    const bnbSettings = useBnbSelector((state) => state.bnbSettings);
    const financeOption = useBnbSelector((state) => state.utils.financeOption);
    const totalPrice = useBnbSelector((state) => state.basket.total);
    const ecoBonus = useBnbSelector((state) =>
        getEcoBonusPromotions<PricePromotionType>(state.items.promotions, state.car.price.promotions),
    );

    const financeOptionSwitchLabel = useBnbOptionalLabel(
        financeOption === "cash" ? "financeOptionShortSwitchMonthly" : "financeOptionShortSwitchCash",
    );

    const showNextGenInteriorDisclaimerSetting = showNextGenInteriorDisclaimer(
        bnbSettings,
        commonSettings,
        car.model.id,
    );
    const ecoTag = getEcoTagType(car.motorization.engine);
    const ecoBadge = getEcoBadgeType(commonSettings, ecoTag, car.model.id);
    const grLogoCheck = getGrLogo(car.model.webtitleSectionName, car.grade.webtitleSectionName);

    const hasBgImage = !!car.model.defaultCCISBackground;

    // both 3d spins
    const webGLSupportLevel = useWebGLSupportLevel();
    const interiorSpinFlags = getInteriorSpinFlags(commonSettings, car, activePersonalization, webGLSupportLevel);
    // exterior 3d spin
    const exteriorSpin3DState = useBnbSelector((state) => state.utils.exteriorSpin3D);
    const enableExterior3dSpin = commonSettings.query.enableWebGLDebug && activePersonalization !== "upholstery"; // currently only testable via queryparam

    const toggleFinanceOption = (): void => {
        const newOption = financeOption === "cash" ? "monthly" : "cash";
        trackFinanceToggle(NavStep.Customize, newOption, "cc-action");
        dispatch(setFinanceOption(newOption));
    };

    const toggleExteriorSpin3D = (): void => {
        dispatch(
            setExteriorSpin3D(
                exteriorSpin3DState === ExteriorSpin3DState.Displayed
                    ? ExteriorSpin3DState.Hidden
                    : ExteriorSpin3DState.Displayed,
            ),
        );
    };

    return (
        <Styles.Wrapper>
            <UnsetZIndexHelper enabled={spinFullscreen} selectors={[MAIN_NAV_CLASSNAME_TOYOTA]} />
            <Styles.ResizeWrapper fullscreen={spinFullscreen} hasBgImage={hasBgImage && !interiorSpinFlags.enabled}>
                <Styles.HeaderWrapper isFullscreen={spinFullscreen} hasLightColors={interiorSpinFlags.enabled}>
                    <Container>
                        <Styles.Header>
                            <Styles.LeftCorner
                                width={{ _: 1 / 2, lg: 3 / 12 }}
                                mb={{ _: 3, md: "auto" }}
                                isFullscreen={spinFullscreen}
                                hasLightColors={interiorSpinFlags.enabled}
                                hasAvailabilityBadges={
                                    car.soldOut || car.availableForOnlineSales || car.availableForOnlineLeasing
                                }
                            >
                                <div>
                                    <Type.H1
                                        headingType="gamma"
                                        mb={1}
                                        mt={interiorSpinFlags.enabled && !spinFullscreen ? { md: 3 } : 0}
                                        color={interiorSpinFlags.enabled ? "lightest" : "dark"}
                                    >
                                        <Type.ModelName
                                            ml={isRTL(commonSettings) ? 1 : 0}
                                            mr={isRTL(commonSettings) ? 0 : 1}
                                        >
                                            {car.model.webtitleSectionName}
                                        </Type.ModelName>
                                        <Type.GradeName>{car.grade.webtitleSectionName}</Type.GradeName>
                                    </Type.H1>
                                    {car.body.webtitleSectionName && (
                                        <Type.BodyTypeName>{car.body.webtitleSectionName}</Type.BodyTypeName>
                                    )}
                                    {!hideCarHeaderPrice && (
                                        <div>
                                            <EcoBonusPromotion ecoBonus={ecoBonus} />
                                            <Price
                                                price={totalPrice}
                                                isLarge
                                                financeItemId="total"
                                                forceMonthlyDisclaimer
                                                hideWhenLoading
                                            />
                                            <PriceFootNote />
                                        </div>
                                    )}
                                    {/* label should only be shown when there's a badge on customize */}
                                    {ecoBadge && ecoTag && <EcoTag type={ecoTag} />}
                                </div>
                                <div>
                                    <AvailabilityBadges
                                        isSoldOut={car.soldOut}
                                        isAvailableForOnlineSales={car.availableForOnlineSales}
                                        isAvailableForOnlineLeasing={car.availableForOnlineLeasing}
                                    />

                                    {financeOptionSwitchLabel && (
                                        <Styles.FinanceToggle onClick={toggleFinanceOption}>
                                            {financeOptionSwitchLabel}
                                        </Styles.FinanceToggle>
                                    )}

                                    {enableExterior3dSpin && (
                                        <Styles.ExteriorSpin3DToggle
                                            onClick={toggleExteriorSpin3D}
                                            display={{ _: "flex", md: "none" }}
                                            disabled={exteriorSpin3DState === ExteriorSpin3DState.Error}
                                        >
                                            <BnbLabel label={exterior3DSpinLabelMap[exteriorSpin3DState]} />
                                            <IconWrapper>
                                                {exteriorSpin3DState === ExteriorSpin3DState.Displayed ? (
                                                    <Icon variant="car" />
                                                ) : (
                                                    <Icon3D />
                                                )}
                                            </IconWrapper>
                                        </Styles.ExteriorSpin3DToggle>
                                    )}
                                </div>
                            </Styles.LeftCorner>
                            <Styles.Center
                                hasLightColor={interiorSpinFlags.enabled}
                                hasAvailabilityBadges={
                                    car.soldOut || car.availableForOnlineSales || car.availableForOnlineLeasing
                                }
                                height={{
                                    _: "auto",
                                    lg: interiorSpinFlags.enabled ? "auto" : 355,
                                }}
                                width={{ _: 1, lg: 7 / 12 }}
                                order={{ _: 3, lg: 2 }}
                            />
                            <Styles.RightCorner
                                width={{ _: 1 / 2, lg: 2 / 12 }}
                                isFullscreen={spinFullscreen}
                                hasLightColor={interiorSpinFlags.enabled}
                                hasAvailabilityBadges={
                                    car.soldOut || car.availableForOnlineSales || car.availableForOnlineLeasing
                                }
                            >
                                <Styles.RightCornerTop>
                                    {grLogoCheck && (
                                        <Styles.GrLogo>
                                            {grLogoCheck === GrCode.Gr ? <GRLogo /> : <GRLogoSport />}
                                        </Styles.GrLogo>
                                    )}

                                    {enableExterior3dSpin && (
                                        <Styles.ExteriorSpin3DToggle
                                            onClick={toggleExteriorSpin3D}
                                            display={{ _: "none", md: "flex" }}
                                            disabled={exteriorSpin3DState === ExteriorSpin3DState.Error}
                                        >
                                            <BnbLabel label={exterior3DSpinLabelMap[exteriorSpin3DState]} />
                                            <IconWrapper>
                                                {exteriorSpin3DState === ExteriorSpin3DState.Displayed ? (
                                                    <Icon variant="close" />
                                                ) : (
                                                    <Icon3D />
                                                )}
                                            </IconWrapper>
                                        </Styles.ExteriorSpin3DToggle>
                                    )}

                                    <Styles.Toggle
                                        type="button"
                                        hasWinterWheel={hasWinterWheel}
                                        isFullscreen={spinFullscreen}
                                        onClick={() => {
                                            setSpinFullscreen((state) => !state);
                                            if (!spinFullscreen) trackCustomizeFullscreen(activePersonalization);
                                        }}
                                    >
                                        <span className="sr-only">
                                            <BnbLabel
                                                label={spinFullscreen ? "spinFullscreenClose" : "spinFullscreen"}
                                            />
                                        </span>
                                        <IconWrapper>
                                            {spinFullscreen ? <Icon variant="close" /> : <Icon variant="fullscreen" />}
                                        </IconWrapper>
                                    </Styles.Toggle>
                                </Styles.RightCornerTop>

                                <Styles.RightCornerBottom>
                                    <CustomizeCtaContainer />
                                    {hasWinterWheel && <WinterWheelToggle />}
                                    {ecoBadge && ecoTag && <EcoTag type={ecoTag} />}
                                </Styles.RightCornerBottom>
                            </Styles.RightCorner>
                            {!interiorSpinFlags.enabled && (
                                <Styles.ExteriorSpinWrapper isFullscreen={spinFullscreen} hasBgImage={hasBgImage}>
                                    {enableExterior3dSpin && exteriorSpin3DState === ExteriorSpin3DState.Displayed ? (
                                        <Exterior3DSpin />
                                    ) : (
                                        <ExteriorSpin isFullscreen={spinFullscreen} />
                                    )}
                                </Styles.ExteriorSpinWrapper>
                            )}
                        </Styles.Header>
                    </Container>
                </Styles.HeaderWrapper>
                <Styles.InteriorGalleryWrapper isFullscreen={spinFullscreen}>
                    {interiorSpinFlags.interior3D && <InteriorSpin3D visible fullscreen={spinFullscreen} />}
                    {interiorSpinFlags.interior2D && <InteriorGallery isFullscreen={spinFullscreen} />}
                    {/* Required as per design, just show a * which references the actual disclaimer underneath the personalisation value see Personalization.tsx */}
                    {activePersonalization === "upholstery" && showNextGenInteriorDisclaimerSetting && (
                        <Styles.DisclaimerReference>*</Styles.DisclaimerReference>
                    )}
                </Styles.InteriorGalleryWrapper>
            </Styles.ResizeWrapper>

            <Container mt={2}>
                <Styles.Toolbar width={1}>
                    {hasWinterWheel && <WinterWheelToggle />}
                    <Personalization />
                    <CustomizeCtaContainer />
                </Styles.Toolbar>
            </Container>
        </Styles.Wrapper>
    );
};

export default CarHero;
